import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import AddShoppingCartRoundedIcon from '@mui/icons-material/AddShoppingCartRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCollection, getId, getSiteLabel, getStockItemLabel, getUserLabel, requestJson, stripEmptyValues, userHasSite } from '../lib/api';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useSiteSession } from '../context/SiteSessionContext';
import { getSocket } from '../lib/socket';
import { playActionSound } from '../lib/sound';
import { useMenuPage } from '../hooks/useMenuPage';
import { useCoalescedRefresh } from '../hooks/useCoalescedRefresh';

const emptyForm = {
  siteId: '',
  barcode: '',
  supplier: '',
  label: '',
  sphere: 0,
  cylinder: 0,
  availableQty: 0,
  reservedQty: 0,
};

const emptyReservationForm = {
  stockItemId: '',
  storeSiteId: '',
  workshopSiteId: '',
  customerLastName: '',
  customerFirstName: '',
  advisorId: '',
  lensQuantity: 0,
  jobNumber: '',
  frameType: 'new',
  mountingLocation: 'store',
  frameDisplayLabel: '',
};

const STOCK_PROCESS_CODES = ['STOCK_LENS_STORE', 'STOCK_LENS_WORKSHOP', 'STOCK_LENS_RAW_BL'];
const DEFAULT_MOUNTING_TYPES = ['T.D.', 'Classique', 'Easy-Digifit', 'Percées', 'V.S.', '+Gravure', 'Clip', 'Traçabilité'];

const CODE_39_PATTERNS = {
  '0': 'nnnwwnwnn', '1': 'wnnwnnnnw', '2': 'nnwwnnnnw', '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw', '5': 'wnnwwnnnn', '6': 'nnwwwnnnn', '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn', '9': 'nnwwnnwnn', A: 'wnnnnwnnw', B: 'nnwnnwnnw',
  C: 'wnwnnwnnn', D: 'nnnnwwnnw', E: 'wnnnwwnnn', F: 'nnwnwwnnn',
  G: 'nnnnnwwnw', H: 'wnnnnwwnn', I: 'nnwnnwwnn', J: 'nnnnwwwnn',
  K: 'wnnnnnnww', L: 'nnwnnnnww', M: 'wnwnnnnwn', N: 'nnnnwnnww',
  O: 'wnnnwnnwn', P: 'nnwnwnnwn', Q: 'nnnnnnwww', R: 'wnnnnnwwn',
  S: 'nnwnnnwwn', T: 'nnnnwnwwn', U: 'wwnnnnnnw', V: 'nwwnnnnnw',
  W: 'wwwnnnnnn', X: 'nwnnwnnnw', Y: 'wwnnwnnnn', Z: 'nwwnwnnnn',
  '-': 'nwnnnnwnw', '*': 'nwnnwnwnn',
};

function getBarcodeSvg(value) {
  const normalized = String(value || '').toUpperCase().replace(/[^0-9A-Z-]/g, '');
  const payload = `*${normalized}*`;
  const narrow = 2;
  const wide = 5;
  const quietZone = 12;
  let x = quietZone;
  const bars = [];
  [...payload].forEach((character) => {
    const pattern = CODE_39_PATTERNS[character];
    if (!pattern) return;
    [...pattern].forEach((widthType, index) => {
      const width = widthType === 'w' ? wide : narrow;
      if (index % 2 === 0) bars.push(`<rect x="${x}" y="4" width="${width}" height="44" fill="#111827"/>`);
      x += width;
    });
    x += narrow;
  });
  const width = x + quietZone;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} 64" preserveAspectRatio="none"><rect width="100%" height="100%" fill="#fff"/>${bars.join('')}<text x="${width / 2}" y="60" text-anchor="middle" font-family="Arial" font-size="10" font-weight="700">${normalized}</text></svg>`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('fr-FR') : '—';
}

function isStockFollowup(row) {
  return row?.lens?.mode === 'stock' || STOCK_PROCESS_CODES.includes(row?.process);
}

function getCurrentWorkflowStep(row) {
  return (row?.workflow?.steps || []).find((step) => !step.completedAt) || null;
}

function formatDiopter(value) {
  const number = Number(value || 0);
  return `${number > 0 ? '+' : ''}${number.toFixed(2)}`;
}

function formatOpticalCorrection(sphere, cylinder) {
  const hasSphere = sphere !== undefined && sphere !== null && sphere !== '';
  const hasCylinder = cylinder !== undefined && cylinder !== null && cylinder !== '';
  if (!hasSphere && !hasCylinder) return '';
  return `${hasSphere ? formatDiopter(sphere) : '—'} (${hasCylinder ? formatDiopter(cylinder) : '—'})`;
}

function formatReservedLens(lens = {}) {
  const stockItem = lens.stockItemId && typeof lens.stockItemId === 'object' ? lens.stockItemId : {};
  const sphere = lens.sphere ?? stockItem.sphere;
  const cylinder = lens.cylinder ?? stockItem.cylinder;
  const hasSphere = sphere !== undefined && sphere !== null && sphere !== '';
  const hasCylinder = cylinder !== undefined && cylinder !== null && cylinder !== '';
  const sideLabel = lens.side === 'right' ? 'OD' : lens.side === 'left' ? 'OG' : '';

  return [
    sideLabel,
    lens.barcode || stockItem.barcode,
    lens.label || stockItem.label,
    formatOpticalCorrection(hasSphere ? sphere : null, hasCylinder ? cylinder : null),
    !sideLabel ? `x${lens.quantity || 1}` : '',
  ].filter(Boolean).join(' · ');
}

function getReservedLensLines(row) {
  if (row?.lens?.stockReservationDetails?.length) return row.lens.stockReservationDetails;
  const stockItem = row?.lens?.stockItemId;
  return stockItem ? [{ ...(typeof stockItem === 'object' ? stockItem : {}), stockItemId: stockItem, quantity: row.lens?.quantity || 1 }] : [];
}

function getReservedLensSides(row) {
  return getReservedLensLines(row)
    .flatMap((lens) => {
      const sides = Array.isArray(lens.sides) ? lens.sides : [];
      return sides.length
        ? sides.map((side) => ({ ...lens, side, quantity: 1 }))
        : [{ ...lens, side: '' }];
    })
    .sort((left, right) => ({ right: 0, left: 1 }[left.side] ?? 2) - ({ right: 0, left: 1 }[right.side] ?? 2));
}

function getSiteCode(site) {
  if (!site) return '';
  if (typeof site === 'string') return site;
  return site.code || getSiteLabel(site);
}

function getAvailableStockColor(value, lowStockThreshold = 2) {
  const qty = Number(value || 0);
  if (qty <= 0) return 'error';
  if (qty <= lowStockThreshold) return 'warning';
  return 'success';
}

function getStockStatusLabel(row, lowStockThreshold = 2) {
  const available = Number(row.availableQty || 0);
  const reserved = Number(row.reservedQty || 0);
  if (available <= 0) return 'Rupture';
  if (available <= lowStockThreshold) return 'Stock bas';
  if (reserved > 0) return 'Réservé';
  return 'Disponible';
}

const centeredCellSx = {
  height: '100%',
  display: 'flex',
  alignItems: 'center',
};

export default function StockLensesPage() {
  const menuPage = useMenuPage('/stock/verres');
  const [searchParams] = useSearchParams();
  const { isAdmin, site } = useSiteSession();
  const canManageStock = isAdmin || site?.type === 'workshop';
  const canReserveStock = isAdmin || site?.type === 'store';
  const canPickStock = site?.type === 'workshop';
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [supplyOpen, setSupplyOpen] = useState(false);
  const [supplyForm, setSupplyForm] = useState({ siteId: '', barcode: '', quantity: 1 });
  const [supplying, setSupplying] = useState(false);
  const [reservationOpen, setReservationOpen] = useState(false);
  const [reservationForm, setReservationForm] = useState(emptyReservationForm);
  const [activeReservations, setActiveReservations] = useState([]);
  const [reserving, setReserving] = useState(false);
  const [open, setOpen] = useState(false);
  const [quickFilter, setQuickFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState({
    supplier: '',
    site: '',
    status: '',
    sphere: '',
    cylinder: '',
  });
  const didApplyDefaultSupplier = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'info', message: '' });
  const [stockSettings, setStockSettings] = useState({ lowStockThreshold: 2, mountingTypes: DEFAULT_MOUNTING_TYPES });
  const [stockFollowups, setStockFollowups] = useState([]);
  const [stockFollowupsLoading, setStockFollowupsLoading] = useState(false);
  const [stockFollowupFilter, setStockFollowupFilter] = useState('all');
  const [pickingOpen, setPickingOpen] = useState(false);
  const [pickingForm, setPickingForm] = useState({ jobNumber: '', firstBarcode: '', secondBarcode: '' });
  const [pickingTarget, setPickingTarget] = useState(null);
  const [pickingSaving, setPickingSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ jobNumber: '', mountingType: '', actionCode: 'WORKSHOP_SEND_STOCK_LENS' });
  const [workflowActionSavingId, setWorkflowActionSavingId] = useState('');
  const [sending, setSending] = useState(false);
  const [workshopSheetPreview, setWorkshopSheetPreview] = useState(null);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const payload = await requestJson('/stock-items');
      setRows(getCollection(payload));
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSites = useCallback(async () => {
    try {
      const payload = await requestJson('/sites');
      setSites(getCollection(payload));
    } catch (_err) {
      setSites([]);
    }
  }, []);

  const loadPeople = useCallback(async () => {
    try {
      const usersPayload = await requestJson('/users?limit=2000');
      setUsers(getCollection(usersPayload));
    } catch (_err) {
      setUsers([]);
    }
  }, []);

  const loadActiveReservations = useCallback(async () => {
    try {
      const payload = await requestJson('/stock-reservations');
      setActiveReservations(getCollection(payload));
    } catch (_err) {
      setActiveReservations([]);
    }
  }, []);

  const loadStockSettings = useCallback(async () => {
    try {
      const payload = await requestJson('/settings/STOCK');
      const mountingTypes = Array.isArray(payload.value?.mountingTypes)
        ? payload.value.mountingTypes.map((value) => String(value || '').trim()).filter(Boolean)
        : [];
      setStockSettings({
        lowStockThreshold: Number(payload.value?.lowStockThreshold ?? 2),
        mountingTypes: mountingTypes.length ? mountingTypes : DEFAULT_MOUNTING_TYPES,
      });
    } catch (_err) {
      setStockSettings({ lowStockThreshold: 2, mountingTypes: DEFAULT_MOUNTING_TYPES });
    }
  }, []);

  const loadStockFollowups = useCallback(async () => {
    if (!canManageStock) {
      setStockFollowups([]);
      return;
    }

    try {
      setStockFollowupsLoading(true);
      const payload = await requestJson('/commandes?view=stock&limit=500');
      setStockFollowups(getCollection(payload).filter(isStockFollowup));
    } catch (_err) {
      setStockFollowups([]);
    } finally {
      setStockFollowupsLoading(false);
    }
  }, [canManageStock]);

  useEffect(() => {
    loadRows();
    loadSites();
    loadPeople();
    loadActiveReservations();
    loadStockSettings();
    loadStockFollowups();
  }, [loadActiveReservations, loadRows, loadPeople, loadSites, loadStockFollowups, loadStockSettings]);

  useEffect(() => {
    if (isAdmin || site?.type !== 'store' || !sites.length || didApplyDefaultSupplier.current) return;

    const connectedStore = sites.find((item) => getId(item) === getId(site));
    const defaultSupplier = connectedStore?.supplierAccounts?.[0]?.supplier || '';
    if (defaultSupplier) {
      setColumnFilters((current) => ({ ...current, supplier: defaultSupplier }));
    }
    didApplyDefaultSupplier.current = true;
  }, [isAdmin, site, sites]);

  useEffect(() => {
    const q = searchParams.get('q') || '';
    if (q) setQuickFilter(q);
  }, [searchParams]);

  const scheduleStockRefresh = useCoalescedRefresh(async () => {
    await Promise.all([loadRows(), loadStockFollowups()]);
  }, 350);

  useEffect(() => {
    const socket = getSocket();
    socket.on('stock-items:changed', scheduleStockRefresh);
    socket.on('commandes:changed', scheduleStockRefresh);

    return () => {
      socket.off('stock-items:changed', scheduleStockRefresh);
      socket.off('commandes:changed', scheduleStockRefresh);
    };
  }, [scheduleStockRefresh]);

  useEffect(() => {
    if (!error) return;
    setSnackbar({ open: true, severity: 'error', message: error });
  }, [error]);

  useEffect(() => {
    if (!snackbar.open || !snackbar.message) return;
    playActionSound(snackbar.severity);
  }, [snackbar.open, snackbar.message, snackbar.severity]);

  const workshopSites = useMemo(() => sites.filter((site) => site.type === 'workshop'), [sites]);
  const defaultWorkshopSiteId = workshopSites.length === 1 ? workshopSites[0]._id : '';
  const storeSites = useMemo(() => sites.filter((site) => site.type === 'store'), [sites]);
  const advisorOptions = useMemo(
    () =>
      isAdmin
        ? users.filter((user) => user.active !== false)
        : users.filter((user) => user.active !== false && userHasSite(user, site?._id)),
    [isAdmin, site, users]
  );

  const stockFilterOptions = useMemo(
    () => ({
      suppliers: [...new Set(rows.map((row) => row.supplier).filter(Boolean))].sort(),
      labels: [...new Set(rows.map((row) => row.label).filter(Boolean))].sort(),
      sites: [...new Set(rows.map((row) => getSiteCode(row.siteId)).filter(Boolean))].sort(),
      statuses: ['Disponible', 'Stock bas', 'Rupture', 'Réservé'],
      spheres: [...new Set(rows.map((row) => formatDiopter(row.sphere)).filter(Boolean))].sort(),
      cylinders: [...new Set(rows.map((row) => formatDiopter(row.cylinder)).filter(Boolean))].sort(),
    }),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const search = quickFilter.trim().toLowerCase();
    const normalizedFilters = Object.fromEntries(
      Object.entries(columnFilters).map(([key, value]) => [key, String(value || '').trim().toLowerCase()])
    );

    return rows.filter((row) => {
      const siteCode = getSiteCode(row.siteId);
      const status = getStockStatusLabel(row, stockSettings.lowStockThreshold);
      const searchable = [
        row.barcode,
        row.supplier,
        row.label,
        siteCode,
        formatDiopter(row.sphere),
        formatDiopter(row.cylinder),
        row.availableQty,
        row.reservedQty,
        status,
      ]
        .join(' ')
        .toLowerCase();

      if (search && !searchable.includes(search)) return false;
      if (normalizedFilters.supplier && !String(row.supplier || '').toLowerCase().includes(normalizedFilters.supplier)) {
        return false;
      }
      if (normalizedFilters.site && !siteCode.toLowerCase().includes(normalizedFilters.site)) return false;
      if (normalizedFilters.status && !status.toLowerCase().includes(normalizedFilters.status)) return false;
      if (normalizedFilters.sphere && !formatDiopter(row.sphere).toLowerCase().includes(normalizedFilters.sphere)) {
        return false;
      }
      if (normalizedFilters.cylinder && !formatDiopter(row.cylinder).toLowerCase().includes(normalizedFilters.cylinder)) {
        return false;
      }
      return true;
    });
  }, [rows, quickFilter, columnFilters, stockSettings.lowStockThreshold]);

  const gridRows = useMemo(
    () =>
      filteredRows.map((row) => ({
        ...row,
        id: row._id,
        siteLabel: getSiteCode(row.siteId),
        statusDisplay: getStockStatusLabel(row, stockSettings.lowStockThreshold),
      })),
    [filteredRows, stockSettings.lowStockThreshold]
  );

  const selectedReservationStockItem = rows.find((row) => row._id === reservationForm.stockItemId) || null;
  const reservationAvailableQuantity = Math.max(Number(selectedReservationStockItem?.availableQty || 0), 0);
  const reservationRequestedQuantity = Number(reservationForm.lensQuantity || 0);
  const reservationQuantityExceeded = reservationRequestedQuantity > reservationAvailableQuantity;
  const reservationQuantityInvalid =
    reservationRequestedQuantity <= 0 || reservationRequestedQuantity > 2 || reservationQuantityExceeded;
  const selectedReservationAdvisor = users.find((user) => user._id === reservationForm.advisorId) || null;

  const scopedStockFollowups = useMemo(
    () =>
      stockFollowups.filter((row) => {
        if (isAdmin) return true;
        return getId(row.workshopSiteId) === site?._id;
      }),
    [isAdmin, site, stockFollowups]
  );
  const stockFollowupActionRows = useMemo(
    () =>
      scopedStockFollowups.filter((row) => {
        const step = getCurrentWorkflowStep(row);
        return step?.code === 'ATELIER_RECUPERE_VERRE_STOCK';
      }),
    [scopedStockFollowups]
  );
  const stockFollowupMatchingRows = useMemo(
    () => scopedStockFollowups.filter((row) =>
      ['ATELIER_MONTAGE_RAPPROCHEMENT', 'ATELIER_RAPPROCHEMENT', 'ATELIER_RAPPROCHEMENT_MONTAGE'].includes(getCurrentWorkflowStep(row)?.code)
    ),
    [scopedStockFollowups]
  );
  const stockFollowupSendRows = useMemo(
    () =>
      scopedStockFollowups.filter(
        (row) => ['ATELIER_PRET_ENVOI_VERRE_MAGASIN', 'ATELIER_PRET_ENVOI_MAGASIN'].includes(getCurrentWorkflowStep(row)?.code)
      ),
    [scopedStockFollowups]
  );
  const stockFollowupDispatchRows = useMemo(
    () => scopedStockFollowups.filter((row) =>
      ['ATELIER_ENVOI_VERRE_MAGASIN', 'ATELIER_ENVOI_LUNETTE_MAGASIN'].includes(getCurrentWorkflowStep(row)?.code)
    ),
    [scopedStockFollowups]
  );
  const stockFollowupTableRows = useMemo(
    () => {
      const activeRows = scopedStockFollowups.filter((row) => getCurrentWorkflowStep(row));
      if (stockFollowupFilter === 'picking') return stockFollowupActionRows;
      if (stockFollowupFilter === 'matching') return stockFollowupMatchingRows;
      if (stockFollowupFilter === 'send') return stockFollowupSendRows;
      if (stockFollowupFilter === 'dispatch') return stockFollowupDispatchRows;
      return activeRows;
    },
    [scopedStockFollowups, stockFollowupActionRows, stockFollowupDispatchRows, stockFollowupFilter, stockFollowupMatchingRows, stockFollowupSendRows]
  );

  useEffect(() => {
    if (!defaultWorkshopSiteId) return;
    setForm((current) => ({ ...current, siteId: current.siteId || defaultWorkshopSiteId }));
    setSupplyForm((current) => ({ ...current, siteId: current.siteId || defaultWorkshopSiteId }));
    setReservationForm((current) => ({ ...current, workshopSiteId: current.workshopSiteId || defaultWorkshopSiteId }));
  }, [defaultWorkshopSiteId]);

  const handleOpenCreate = () => {
    setForm({ ...emptyForm, siteId: !isAdmin ? site?._id || '' : defaultWorkshopSiteId });
    setEditingId(null);
    setOpen(true);
  };

  const handleOpenSupply = () => {
    setSupplyForm({ siteId: !isAdmin ? site?._id || '' : defaultWorkshopSiteId, barcode: '', quantity: 1 });
    setSupplyOpen(true);
  };

  const handlePrintWorkshopSheet = (row) => {
    const printWindow = window.open('', '_blank', 'width=980,height=760');
    if (!printWindow) {
      setSnackbar({ open: true, severity: 'error', message: 'Autorise les fenêtres surgissantes pour imprimer la fiche atelier.' });
      return;
    }

    const jobNumber = row.workshopSheetNumber || row.jobNumber || '';
    const client = row.customerSnapshot?.fullName || row.customerFullName || '—';
    const advisor = row.advisorSnapshot?.fullName || getUserLabel(row.advisorId) || '—';
    const lensLines = getReservedLensSides(row);
    const lensHtml = lensLines.map((lens, index) => {
      const stockItem = lens.stockItemId && typeof lens.stockItemId === 'object' ? lens.stockItemId : {};
      const barcode = lens.barcode || stockItem.barcode || '';
      const eyeLabel = lens.side === 'right' ? 'OD — Œil droit' : lens.side === 'left' ? 'OG — Œil gauche' : `Verre ${index + 1}`;
      return `<section class="lens"><div><strong>${eyeLabel}</strong><p>${escapeHtml(formatReservedLens(lens))}</p></div><div class="barcode">${getBarcodeSvg(barcode)}</div></section>`;
    }).join('');
    const workflowHtml = (row.workflow?.steps || []).map((step) => `
      <tr><td>${escapeHtml(step.label || step.code)}</td><td>${escapeHtml(step.ownerType === 'workshop' ? 'Atelier' : 'Magasin')}</td><td>${escapeHtml(formatDateTime(step.completedAt))}</td></tr>
    `).join('');

    printWindow.document.open();
    printWindow.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Fiche atelier ${escapeHtml(jobNumber)}</title><style>
      @page{size:A4;margin:12mm}body{font-family:Arial,sans-serif;color:#111827;margin:0;font-size:12px}h1{margin:0 0 4px;font-size:24px}.print-header{display:flex;justify-content:space-between;align-items:flex-start;gap:20px}.client-header{text-align:right;font-size:24px;font-weight:700}.sub{color:#475569;margin-bottom:16px}.job{border:2px solid #0f172a;border-radius:10px;padding:10px;margin-bottom:14px;text-align:center}.job-barcode svg{width:180px;height:38px}.grid,.lenses{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin:14px 0}.field{border:1px solid #cbd5e1;border-radius:7px;padding:9px}.label{display:block;color:#64748b;font-size:10px;font-weight:700;text-transform:uppercase;margin-bottom:4px}.lens{display:grid;grid-template-rows:auto 48px;gap:8px;border:1px solid #94a3b8;border-radius:8px;padding:10px}.lens p{margin:5px 0 0}.lens .barcode svg{width:100%;height:48px}table{width:100%;border-collapse:collapse;margin-top:14px}th,td{border:1px solid #cbd5e1;padding:7px;text-align:left}th{background:#e2e8f0}.footer{margin-top:24px;display:grid;grid-template-columns:1fr 1fr;gap:30px}.signature{height:70px;border-top:1px solid #64748b;padding-top:5px}
    </style></head><body><div class="print-header"><h1>Fiche atelier — Verres de stock</h1><div class="client-header">${escapeHtml(client)}</div></div><div class="sub">Optibox · Imprimée le ${escapeHtml(formatDateTime(new Date()))}</div><section class="job"><div class="job-barcode">${getBarcodeSvg(jobNumber)}</div></section><div class="grid"><div class="field"><span class="label">Client</span>${escapeHtml(client)}</div><div class="field"><span class="label">Conseiller</span>${escapeHtml(advisor)}</div><div class="field"><span class="label">Magasin</span>${escapeHtml(getSiteLabel(row.storeSiteId))}</div><div class="field"><span class="label">Atelier</span>${escapeHtml(getSiteLabel(row.workshopSiteId))}</div><div class="field"><span class="label">Création</span>${escapeHtml(formatDateTime(row.createdAt))}</div><div class="field"><span class="label">Dernière mise à jour</span>${escapeHtml(formatDateTime(row.updatedAt))}</div><div class="field"><span class="label">Étape actuelle</span>${escapeHtml(getCurrentWorkflowStep(row)?.label || 'Terminé')}</div><div class="field"><span class="label">Type de montage</span>${escapeHtml(row.frame?.mountingType || '—')}</div></div><h2>Verres réservés</h2><div class="lenses">${lensHtml || '<p>Aucun verre réservé.</p>'}</div><h2>Historique du workflow</h2><table><thead><tr><th>Étape</th><th>Responsable</th><th>Date</th></tr></thead><tbody>${workflowHtml}</tbody></table><div class="footer"><div class="signature">Technicien</div><div class="signature">Contrôle</div></div><script>window.onload=()=>{window.print();};</script></body></html>`);
    printWindow.document.close();
    printWindow.opener = null;
  };

  const handleOpenReservation = useCallback(
    (row) => {
      const workshopSiteId = getId(row.siteId);
      const storeSiteId = !isAdmin && site?.type === 'store' ? site?._id || '' : '';
      const latestReservation = activeReservations
        .filter((reservation) => getId(reservation.storeSiteId) === storeSiteId)
        .sort((left, right) => new Date(right.reservedAt || right.createdAt || 0) - new Date(left.reservedAt || left.createdAt || 0))[0];
      const customerNameParts = String(latestReservation?.customerSnapshot?.fullName || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      setReservationForm({
        ...emptyReservationForm,
        stockItemId: row._id,
        storeSiteId,
        workshopSiteId: workshopSiteId || defaultWorkshopSiteId,
        lensQuantity: 0,
        frameDisplayLabel: getStockItemLabel(row),
        customerFirstName: customerNameParts[0] || '',
        customerLastName: customerNameParts.slice(1).join(' '),
        advisorId: getId(latestReservation?.advisorId),
      });
      setReservationOpen(true);
      setError('');
    },
    [activeReservations, defaultWorkshopSiteId, isAdmin, site]
  );

  const handleOpenEdit = useCallback((row) => {
    setForm({
      siteId: getId(row.siteId),
      barcode: row.barcode || '',
      supplier: row.supplier || '',
      label: row.label || '',
      sphere: row.sphere ?? 0,
      cylinder: row.cylinder ?? 0,
      availableQty: row.availableQty ?? 0,
      reservedQty: row.reservedQty ?? 0,
    });
    setEditingId(row._id);
    setOpen(true);
  }, []);

  const handleOpenPicking = useCallback((row = null) => {
    setPickingTarget(row);
    setPickingForm({
      jobNumber: row?.workshopSheetNumber || row?.jobNumber || '',
      firstBarcode: '',
      secondBarcode: '',
    });
    setPickingOpen(true);
  }, []);

  const handleOpenSend = useCallback((row = null) => {
    const stepCode = getCurrentWorkflowStep(row)?.code;
    setSendForm({
      jobNumber: row?.workshopSheetNumber || row?.jobNumber || '',
      mountingType: '',
      actionCode: stepCode === 'ATELIER_PRET_ENVOI_MAGASIN' ? 'WORKSHOP_SEND' : 'WORKSHOP_SEND_STOCK_LENS',
    });
    setSendOpen(true);
    setError('');
  }, []);

  const handleSendSubmit = async (event) => {
    event.preventDefault();
    const jobNumber = sendForm.jobNumber.trim();
    if (!jobNumber || !stockSettings.mountingTypes.includes(sendForm.mountingType)) return;

    try {
      setSending(true);
      setError('');
      const payload = await requestJson('/commandes/workflow/bulk-complete', {
        method: 'POST',
        body: JSON.stringify({
          actionCode: sendForm.actionCode,
          jobNumbers: [jobNumber],
          mountingType: sendForm.mountingType,
        }),
      });
      const result = payload.results?.[0];
      if (!result?.success) throw new Error(result?.message || 'Envoi verre impossible.');
      setSendOpen(false);
      setSnackbar({ open: true, severity: 'success', message: `Envoi verre préparé pour le JOB ${result.jobNumber}.` });
      await loadStockFollowups();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleCompleteMatching = async (row) => {
    const step = getCurrentWorkflowStep(row);
    if (!step) return;
    try {
      setWorkflowActionSavingId(row._id);
      setError('');
      const updated = await requestJson(`/commandes/${row._id}/workflow/steps/${step.code}/complete`, {
        method: 'PATCH',
        body: JSON.stringify({}),
      });
      setSnackbar({ open: true, severity: 'success', message: `Rapprochement validé pour le JOB ${updated.jobNumber}.` });
      await loadStockFollowups();
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setWorkflowActionSavingId('');
    }
  };

  const handleCreateReturnBl = async (row) => {
    try {
      setWorkflowActionSavingId(row._id);
      setError('');
      const payload = await requestJson('/commandes/workflow/dispatch-lists/auto-return', {
        method: 'POST',
        body: JSON.stringify({ commandeIds: [row._id] }),
      });
      setSnackbar({
        open: true,
        severity: payload.createdCount ? 'success' : 'info',
        message: payload.createdCount
          ? `BL retour ${payload.lists?.[0]?.listNumber || ''} créé pour le JOB ${row.jobNumber}.`
          : 'Ce JOB est déjà dans un BL retour actif.',
      });
      await loadStockFollowups();
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setWorkflowActionSavingId('');
    }
  };

  const handlePickingSubmit = async (event) => {
    event.preventDefault();
    try {
      setPickingSaving(true);
      setError('');
      const updated = await requestJson('/commandes/workflow/pick-stock-lenses', {
        method: 'POST',
        body: JSON.stringify({
          jobNumber: pickingForm.jobNumber,
          rightBarcode: pickingForm.firstBarcode,
          leftBarcode: pickingForm.secondBarcode,
        }),
      });
      setPickingOpen(false);
      setPickingTarget(null);
      setSnackbar({ open: true, severity: 'success', message: `Picking validé pour le JOB ${updated.jobNumber}.` });
      await Promise.all([loadRows(), loadStockFollowups()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setPickingSaving(false);
    }
  };

  const handleChange = (event) => {
    const { name, value, type } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError('');
      await requestJson(editingId ? `/stock-items/${editingId}` : '/stock-items', {
        method: editingId ? 'PUT' : 'POST',
        body: JSON.stringify(stripEmptyValues({ ...form, productType: 'lens' })),
      });
      setOpen(false);
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSupplySubmit = async (event) => {
    event.preventDefault();
    try {
      setSupplying(true);
      setError('');
      await requestJson('/stock-items/supply', {
        method: 'POST',
        body: JSON.stringify(stripEmptyValues(supplyForm)),
      });
      setSupplyOpen(false);
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSupplying(false);
    }
  };

  const handleReservationSubmit = async (event) => {
    event.preventDefault();

    if (!reservationForm.storeSiteId) {
      setError('Sélectionne le magasin demandeur.');
      return;
    }
    if (!reservationForm.customerLastName.trim() || !reservationForm.customerFirstName.trim()) {
      setError('Renseigne le nom et le prénom du client avant de réserver.');
      return;
    }
    if (!reservationForm.advisorId) {
      setError('Sélectionne le conseiller avant de réserver.');
      return;
    }

    try {
      setReserving(true);
      setError('');

      const selectedStockItem = rows.find((row) => row._id === reservationForm.stockItemId);
      const requestedQuantity = Number(reservationForm.lensQuantity);
      if (!selectedStockItem) throw new Error('Verre de stock introuvable.');
      if (!Number.isFinite(requestedQuantity) || requestedQuantity <= 0) {
        throw new Error('Saisis une quantité à réserver supérieure à 0.');
      }
      if (requestedQuantity > 2) {
        throw new Error('Une réservation ne peut pas dépasser 2 verres.');
      }
      if (requestedQuantity > Number(selectedStockItem.availableQty || 0)) {
        throw new Error(`Stock insuffisant : ${selectedStockItem.availableQty || 0} verre(s) disponible(s).`);
      }

      await requestJson('/stock-reservations', {
        method: 'POST',
        body: JSON.stringify(
          stripEmptyValues({
            storeSiteId: reservationForm.storeSiteId,
            stockItemId: reservationForm.stockItemId,
            customerFullName: [reservationForm.customerFirstName.trim(), reservationForm.customerLastName.trim()].filter(Boolean).join(' '),
            advisorId: reservationForm.advisorId,
            advisorFullName: selectedReservationAdvisor ? getUserLabel(selectedReservationAdvisor) : '',
            quantity: requestedQuantity,
          })
        ),
      });

      setReservationOpen(false);
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Verre réservé. Il est disponible dans Verres de stock > Réservations.',
      });
      await Promise.all([loadRows(), loadActiveReservations()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setReserving(false);
    }
  };

  const handleDelete = useCallback(async (id) => {
    try {
      setError('');
      await requestJson(`/stock-items/${id}`, { method: 'DELETE' });
      setDeleteTarget(null);
      await loadRows();
    } catch (err) {
      setError(err.message);
    }
  }, [loadRows]);

  const columns = useMemo(
    () => [
      ...(canReserveStock
        ? [
            {
              field: 'reserveAction',
              headerName: 'Réserver',
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              minWidth: 145,
              align: 'center',
              headerAlign: 'center',
              renderCell: (params) => (
                <Box sx={{ ...centeredCellSx, justifyContent: 'center' }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddShoppingCartRoundedIcon fontSize="small" />}
                    disabled={Number(params.row.availableQty || 0) <= 0}
                    onClick={() => handleOpenReservation(params.row)}
                    sx={{
                      minWidth: 118,
                      fontWeight: 950,
                      letterSpacing: 0,
                    }}
                  >
                    RESERVER
                  </Button>
                </Box>
              ),
            },
          ]
        : []),
      {
        field: 'barcode',
        headerName: 'Code barre',
        minWidth: 165,
        flex: 0.85,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} alignItems="center" sx={centeredCellSx}>
            <QrCodeScannerIcon fontSize="small" color="primary" />
            <Typography variant="body2" sx={{ fontWeight: 950, letterSpacing: '0.02em' }}>
              {params.value}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'supplier',
        headerName: 'Fournisseur',
        minWidth: 135,
        flex: 0.65,
        renderCell: (params) => (
          <Box sx={centeredCellSx}>
            <Chip
              size="small"
              label={params.value || '—'}
              sx={{ bgcolor: 'rgba(14, 165, 233, 0.18)', color: 'primary.light', fontWeight: 850 }}
            />
          </Box>
        ),
      },
      {
        field: 'label',
        headerName: 'Libellé',
        minWidth: 320,
        flex: 1.45,
        renderCell: (params) => (
          <Box sx={{ ...centeredCellSx, minWidth: 0, width: '100%' }}>
            <Typography variant="body2" noWrap title={params.value} sx={{ fontWeight: 700 }}>
              {params.value || '—'}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'siteLabel',
        headerName: 'Atelier',
        minWidth: 120,
        flex: 0.45,
        renderCell: (params) => (
          <Box sx={centeredCellSx}>
            <Chip size="small" label={params.value || '—'} color="secondary" variant="outlined" sx={{ fontWeight: 850 }} />
          </Box>
        ),
      },
      {
        field: 'sphere',
        headerName: 'Sphère',
        type: 'number',
        minWidth: 105,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value) => formatDiopter(value),
      },
      {
        field: 'cylinder',
        headerName: 'Cylindre',
        type: 'number',
        minWidth: 105,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value) => formatDiopter(value),
      },
      {
        field: 'availableQty',
        headerName: 'Disponible',
        type: 'number',
        minWidth: 150,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          return (
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={centeredCellSx}>
              <Chip
                size="small"
                label={params.value ?? 0}
                color={getAvailableStockColor(params.value, stockSettings.lowStockThreshold)}
                sx={{ minWidth: 40, fontWeight: 950 }}
              />
            </Stack>
          );
        },
      },
      {
        field: 'reservedQty',
        headerName: 'Réservé',
        type: 'number',
        minWidth: 115,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Box sx={{ ...centeredCellSx, justifyContent: 'center' }}>
            <Chip
              size="small"
              label={params.value ?? 0}
              color={Number(params.value || 0) > 0 ? 'warning' : 'default'}
              variant={Number(params.value || 0) > 0 ? 'filled' : 'outlined'}
              sx={{ minWidth: 40, fontWeight: 900 }}
            />
          </Box>
        ),
      },
      ...(canManageStock
        ? [
            {
              field: 'actions',
              headerName: 'Action',
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              align: 'right',
              headerAlign: 'right',
              minWidth: 120,
              renderCell: (params) => (
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ width: '100%' }}>
                  <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteTarget(params.row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ),
            },
          ]
        : []),
    ],
    [canManageStock, canReserveStock, handleOpenEdit, handleOpenReservation, stockSettings.lowStockThreshold]
  );

  return (
    <Stack spacing={3}>
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 850 }}>
              {menuPage.sectionTitle}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 950 }}>
              {menuPage.pageTitle}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end" sx={{ rowGap: 1 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadRows();
                loadStockFollowups();
              }}
              sx={{ fontWeight: 850 }}
            >
              Rafraîchir
            </Button>
            {canManageStock && (
              <Button
                variant="contained"
                size="large"
                startIcon={<Inventory2OutlinedIcon />}
                onClick={handleOpenSupply}
                sx={{
                  color: '#ffffff',
                  background: 'linear-gradient(135deg, #16a34a, #059669)',
                  fontWeight: 900,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #16a34a, #059669)',
                    filter: 'brightness(1.08)',
                  },
                }}
              >
                Approvisionner
              </Button>
            )}
            {canManageStock && (
              <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={{ fontWeight: 900 }}>
                Ajouter
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
          gap: 1.5,
        }}
      >
        {[
          ...(canPickStock
            ? [
                {
                  label: 'En attente de picking',
                  value: stockFollowupActionRows.length,
                  detail: 'Verres réservés à récupérer dans le stock',
                  icon: <QrCodeScannerIcon />,
                  color: '#0ea5e9',
                  background: 'rgba(14, 165, 233, 0.15)',
                  filter: 'picking',
                },
                {
                  label: 'En attente de rapprochement',
                  value: stockFollowupMatchingRows.length,
                  detail: 'Monture et verres à rapprocher par l’atelier',
                  icon: <BuildCircleRoundedIcon />,
                  color: '#8b5cf6',
                  background: 'rgba(139, 92, 246, 0.14)',
                  filter: 'matching',
                },
                {
                  label: 'En attente d’envoi verre',
                  value: stockFollowupSendRows.length,
                  detail: 'Type de montage à renseigner avant le retour',
                  icon: <LocalShippingRoundedIcon />,
                  color: '#f59e0b',
                  background: 'rgba(245, 158, 11, 0.14)',
                  filter: 'send',
                },
                {
                  label: 'En attente de BL retour',
                  value: stockFollowupDispatchRows.length,
                  detail: 'Dossiers prêts à intégrer dans un BL atelier',
                  icon: <LocalShippingRoundedIcon />,
                  color: '#10b981',
                  background: 'rgba(16, 185, 129, 0.14)',
                  filter: 'dispatch',
                },
              ]
            : []),
        ].map((card) => (
          <Card
            key={card.label}
            elevation={0}
            onClick={() => setStockFollowupFilter((current) => current === card.filter ? 'all' : card.filter)}
            sx={{
              border: '1px solid',
              borderColor: stockFollowupFilter === card.filter ? card.color : 'divider',
              bgcolor: 'background.paper',
              borderRadius: 2,
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
              boxShadow: stockFollowupFilter === card.filter ? `0 0 0 2px ${card.background}` : 'none',
              '&:hover': { transform: 'translateY(-2px)', borderColor: card.color },
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 900, lineHeight: 1.1 }}>
                    {card.label}
                  </Typography>
                  <Typography variant="h3" sx={{ mt: 0.5, fontWeight: 950, lineHeight: 1 }}>
                    {card.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {card.detail}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: card.background,
                    color: card.color,
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      {canPickStock && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                Bippage rapide atelier
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<QrCodeScannerIcon />}
                  onClick={() => handleOpenPicking()}
                  disabled={!stockFollowupActionRows.length}
                  sx={{ fontWeight: 900, textTransform: 'none' }}
                >
                  Picking verre
                </Button>
                <Button
                  variant="contained"
                  startIcon={<LocalShippingRoundedIcon />}
                  onClick={() => handleOpenSend()}
                  disabled={!stockFollowupSendRows.length}
                  sx={{
                    color: '#ffffff',
                    background: 'linear-gradient(135deg, #2563eb, #06b6d4)',
                    fontWeight: 900,
                    textTransform: 'none',
                    '&:hover': { background: 'linear-gradient(135deg, #2563eb, #06b6d4)', filter: 'brightness(1.08)' },
                  }}
                >
                  Envoi verre
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {canPickStock && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'rgba(14, 165, 233, 0.15)', color: 'primary.main' }}>
                    <QrCodeScannerIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>Suivi - Verres de stock</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stockFollowupTableRows.length} dossier(s) actif(s) rattaché(s) à l’atelier.
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, maxHeight: 360 }}>
                <Table stickyHeader size="small" sx={{ minWidth: 980 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 150 }}>Action</TableCell>
                      <TableCell>JOB</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Conseiller</TableCell>
                      <TableCell>Étape</TableCell>
                      <TableCell>Verre(s) réservé(s)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stockFollowupsLoading ? (
                      <TableRow><TableCell colSpan={6} align="center">Chargement des dossiers…</TableCell></TableRow>
                    ) : stockFollowupTableRows.length ? (
                      stockFollowupTableRows.map((row) => {
                        const step = getCurrentWorkflowStep(row);
                        const reservedLenses = getReservedLensSides(row);
                        const client = row.customerSnapshot?.fullName || row.customerFullName || '—';
                        const advisor = row.advisorSnapshot?.fullName || getUserLabel(row.advisorId) || '—';
                        const canPickRow = step?.code === 'ATELIER_RECUPERE_VERRE_STOCK';
                        const canMatchRow = [
                          'ATELIER_MONTAGE_RAPPROCHEMENT',
                          'ATELIER_RAPPROCHEMENT',
                          'ATELIER_RAPPROCHEMENT_MONTAGE',
                        ].includes(step?.code);
                        const canSendRow = [
                          'ATELIER_PRET_ENVOI_VERRE_MAGASIN',
                          'ATELIER_PRET_ENVOI_MAGASIN',
                        ].includes(step?.code);
                        const canAddToBl = [
                          'ATELIER_ENVOI_VERRE_MAGASIN',
                          'ATELIER_ENVOI_LUNETTE_MAGASIN',
                        ].includes(step?.code);
                        const actionSaving = workflowActionSavingId === row._id;
                        return (
                          <TableRow key={row._id} hover>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                {canPickRow ? (
                                  <Button size="small" variant="contained" startIcon={<QrCodeScannerIcon fontSize="small" />} onClick={() => handleOpenPicking(row)} sx={{ fontWeight: 850, whiteSpace: 'nowrap' }}>
                                    Picking
                                  </Button>
                                ) : canMatchRow ? (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={actionSaving ? <CircularProgress size={14} color="inherit" /> : <BuildCircleRoundedIcon fontSize="small" />}
                                    onClick={() => handleCompleteMatching(row)}
                                    disabled={actionSaving}
                                    sx={{ fontWeight: 850, whiteSpace: 'nowrap' }}
                                  >
                                    Rapprocher
                                  </Button>
                                ) : canSendRow ? (
                                  <Button size="small" variant="contained" startIcon={<LocalShippingRoundedIcon fontSize="small" />} onClick={() => handleOpenSend(row)} sx={{ fontWeight: 850, whiteSpace: 'nowrap' }}>
                                    Envoi verre
                                  </Button>
                                ) : canAddToBl ? (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<LocalShippingRoundedIcon fontSize="small" />}
                                    onClick={() => handleCreateReturnBl(row)}
                                    disabled={actionSaving}
                                    sx={{ fontWeight: 850, whiteSpace: 'nowrap', color: '#ffffff', background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)' }}
                                  >
                                    {actionSaving ? 'Création…' : 'Créer BL retour'}
                                  </Button>
                                ) : (
                                  <Chip size="small" label={step?.ownerType === 'workshop' ? 'Action atelier' : 'En attente magasin'} variant="outlined" />
                                )}
                                <Tooltip title="Voir la fiche atelier">
                                  <IconButton size="small" onClick={() => setWorkshopSheetPreview(row)}><VisibilityIcon fontSize="small" /></IconButton>
                                </Tooltip>
                                <Tooltip title="Imprimer la fiche atelier">
                                  <IconButton size="small" color="primary" onClick={() => handlePrintWorkshopSheet(row)}><PrintRoundedIcon fontSize="small" /></IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 900 }}>{row.workshopSheetNumber || row.jobNumber || '—'}</TableCell>
                            <TableCell>{client}</TableCell>
                            <TableCell>{advisor}</TableCell>
                            <TableCell>
                              <Chip size="small" label={step?.label || 'Terminé'} color={step?.ownerType === 'workshop' ? 'primary' : 'default'} variant="outlined" />
                            </TableCell>
                            <TableCell sx={{ minWidth: 390, maxWidth: 520 }}>
                              <Box sx={{ display: 'grid', gridTemplateColumns: reservedLenses.some((lens) => lens.side) ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 1 }}>
                                {reservedLenses.map((lens, index) => (
                                  <Typography key={`${row._id}-lens-${lens.side || index}`} variant="body2" sx={{ fontWeight: lens.side ? 750 : 400 }}>
                                    {formatReservedLens(lens) || getStockItemLabel(lens.stockItemId) || '—'}
                                  </Typography>
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow><TableCell colSpan={6} align="center">Aucun dossier verre de stock actif pour cet atelier.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 2 }}>
        <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterAltRoundedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Filtres
                </Typography>
              </Stack>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setQuickFilter('');
                  setColumnFilters({ supplier: '', site: '', status: '', sphere: '', cylinder: '' });
                }}
                sx={{ minHeight: 32 }}
              >
                Réinitialiser
              </Button>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(auto-fit, minmax(150px, 1fr))',
                },
                gap: 1.5,
              }}
            >
              <TextField
                size="small"
                label="Recherche globale"
                value={quickFilter}
                onChange={(event) => setQuickFilter(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
              {[
                ['supplier', 'Fournisseur', stockFilterOptions.suppliers],
                ['site', 'Atelier', stockFilterOptions.sites],
                ['status', 'Statut stock', stockFilterOptions.statuses],
                ['sphere', 'Sphère', stockFilterOptions.spheres],
                ['cylinder', 'Cylindre', stockFilterOptions.cylinders],
              ].map(([key, label, options]) => (
                <Autocomplete
                  key={key}
                  freeSolo
                  size="small"
                  options={options}
                  value={columnFilters[key] || ''}
                  onInputChange={(_event, value) =>
                    setColumnFilters((current) => ({ ...current, [key]: value || '' }))
                  }
                  onChange={(_event, value) =>
                    setColumnFilters((current) => ({ ...current, [key]: value || '' }))
                  }
                  renderInput={(params) => <TextField {...params} label={label} />}
                  fullWidth
                />
              ))}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Box sx={{ height: 620, width: '100%' }}>
            <DataGrid
              rows={gridRows}
              columns={columns}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              sx={{
                border: 0,
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-cellContent': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
              }}
            />
          </Box>
        )}
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>{editingId ? 'Modifier un verre' : 'Ajouter un verre'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField name="siteId" label="Atelier" select value={form.siteId} onChange={handleChange} required disabled={!isAdmin}>
                {workshopSites.map((site) => (
                  <MenuItem key={site._id} value={site._id}>
                    {getSiteLabel(site)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField name="barcode" label="Code barre" value={form.barcode} onChange={handleChange} required />
              <Autocomplete
                freeSolo
                options={stockFilterOptions.suppliers}
                value={form.supplier || ''}
                inputValue={form.supplier || ''}
                onInputChange={(_event, value) => setForm((current) => ({ ...current, supplier: value }))}
                onChange={(_event, value) => setForm((current) => ({ ...current, supplier: typeof value === 'string' ? value : value || '' }))}
                renderInput={(params) => <TextField {...params} label="Fournisseur" required />}
              />
              <Autocomplete
                freeSolo
                options={stockFilterOptions.labels}
                value={form.label || ''}
                inputValue={form.label || ''}
                onInputChange={(_event, value) => setForm((current) => ({ ...current, label: value }))}
                onChange={(_event, value) => setForm((current) => ({ ...current, label: typeof value === 'string' ? value : value || '' }))}
                renderInput={(params) => <TextField {...params} label="Libellé" required />}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField name="sphere" label="Sphère" type="number" inputProps={{ step: 0.25 }} value={form.sphere} onChange={handleChange} fullWidth />
                <TextField name="cylinder" label="Cylindre" type="number" inputProps={{ step: 0.25 }} value={form.cylinder} onChange={handleChange} fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField name="availableQty" label="Qté disponible" type="number" value={form.availableQty} onChange={handleChange} fullWidth />
                {isAdmin && (
                  <TextField
                    name="reservedQty"
                    label="Qté réservée"
                    type="number"
                    value={form.reservedQty}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                    fullWidth
                  />
                )}
              </Stack>
              <Alert severity="info">
                La quantité réservée est calculée automatiquement via les commandes. Seule la Direction peut l’ajuster manuellement.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={reservationOpen} onClose={() => setReservationOpen(false)} fullWidth maxWidth="md">
        <Box component="form" onSubmit={handleReservationSubmit}>
          <DialogTitle>Réserver un verre de stock</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <StorefrontRoundedIcon color="primary" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Magasin</Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      {(isAdmin || site?.type !== 'store') && (
                        <TextField
                          name="storeSiteId"
                          label="Magasin"
                          select
                          value={reservationForm.storeSiteId}
                          onChange={(event) =>
                            setReservationForm((current) => ({ ...current, storeSiteId: event.target.value }))
                          }
                          required
                          fullWidth
                        >
                          {storeSites.map((storeSite) => (
                            <MenuItem key={storeSite._id} value={storeSite._id}>
                              {[storeSite.code, getSiteLabel(storeSite)].filter(Boolean).join(' - ')}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      <Autocomplete
                        options={advisorOptions}
                        value={selectedReservationAdvisor}
                        onChange={(_event, value) =>
                          setReservationForm((current) => ({ ...current, advisorId: value?._id || '' }))
                        }
                        getOptionLabel={getUserLabel}
                        renderInput={(params) => <TextField {...params} label="Conseiller" required />}
                        fullWidth
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PersonRoundedIcon color="primary" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Client</Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="Nom client"
                        value={reservationForm.customerLastName}
                        onChange={(event) =>
                          setReservationForm((current) => ({ ...current, customerLastName: event.target.value }))
                        }
                        required
                        fullWidth
                      />
                      <TextField
                        label="Prénom client"
                        value={reservationForm.customerFirstName}
                        onChange={(event) =>
                          setReservationForm((current) => ({ ...current, customerFirstName: event.target.value }))
                        }
                        required
                        fullWidth
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Inventory2OutlinedIcon color="primary" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Article</Typography>
                    </Stack>
                    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                      <Table size="small" sx={{ minWidth: 720 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Code barre</TableCell>
                            <TableCell>Fournisseur</TableCell>
                            <TableCell>Libellé</TableCell>
                            <TableCell align="right">Sphère</TableCell>
                            <TableCell align="right">Cylindre</TableCell>
                            <TableCell sx={{ width: 150 }}>Quantité</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800 }}>{selectedReservationStockItem?.barcode || '—'}</TableCell>
                            <TableCell>{selectedReservationStockItem?.supplier || '—'}</TableCell>
                            <TableCell sx={{ minWidth: 230 }}>{selectedReservationStockItem?.label || '—'}</TableCell>
                            <TableCell align="right">{selectedReservationStockItem ? formatDiopter(selectedReservationStockItem.sphere) : '—'}</TableCell>
                            <TableCell align="right">{selectedReservationStockItem ? formatDiopter(selectedReservationStockItem.cylinder) : '—'}</TableCell>
                            <TableCell>
                              <TextField
                                aria-label="Quantité"
                                type="number"
                                size="small"
                                value={reservationForm.lensQuantity}
                                onChange={(event) =>
                                  setReservationForm((current) => ({ ...current, lensQuantity: Number(event.target.value) }))
                                }
                                inputProps={{ min: 0, max: Math.min(reservationAvailableQuantity, 2) }}
                                error={reservationQuantityInvalid}
                                required
                                fullWidth
                              />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Stack spacing={1}>
                      {reservationQuantityInvalid && (
                        <Typography variant="caption" color="error.main" sx={{ fontWeight: 700 }}>
                          {reservationQuantityExceeded
                            ? `Quantité dépassée : maximum disponible ${reservationAvailableQuantity}.`
                            : reservationRequestedQuantity > 2
                              ? 'Une réservation est limitée à 2 verres.'
                              : 'Quantité obligatoire : 1 ou 2.'}
                        </Typography>
                      )}
                      <Alert severity={reservationQuantityExceeded ? 'error' : 'info'}>
                        {reservationQuantityExceeded
                          ? `Quantité maximale : ${reservationAvailableQuantity}.`
                          : `Quantité disponible : ${reservationAvailableQuantity}.`}
                      </Alert>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReservationOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={reserving || reservationQuantityInvalid}>
              {reserving ? 'Réservation…' : 'Réserver'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(workshopSheetPreview)} onClose={() => setWorkshopSheetPreview(null)} fullWidth maxWidth="md">
        <DialogTitle>Fiche atelier — {workshopSheetPreview?.workshopSheetNumber || workshopSheetPreview?.jobNumber || 'JOB'}</DialogTitle>
        <DialogContent dividers>
          {workshopSheetPreview && (
            <Stack spacing={2}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                {[
                  ['Client', workshopSheetPreview.customerSnapshot?.fullName || workshopSheetPreview.customerFullName],
                  ['Conseiller', workshopSheetPreview.advisorSnapshot?.fullName || getUserLabel(workshopSheetPreview.advisorId)],
                  ['Magasin', getSiteLabel(workshopSheetPreview.storeSiteId)],
                  ['Atelier', getSiteLabel(workshopSheetPreview.workshopSiteId)],
                  ['Création', formatDateTime(workshopSheetPreview.createdAt)],
                  ['Dernière mise à jour', formatDateTime(workshopSheetPreview.updatedAt)],
                  ['Étape actuelle', getCurrentWorkflowStep(workshopSheetPreview)?.label || 'Terminé'],
                  ['Type de montage', workshopSheetPreview.frame?.mountingType],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{value || '—'}</Typography>
                  </Box>
                ))}
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Verres réservés</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                  {getReservedLensSides(workshopSheetPreview).map((lens, index) => (
                    <Box key={`${lens._id || index}-${lens.side || 'lens'}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatReservedLens(lens)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Historique du workflow</Typography>
                <Stack spacing={0.75}>
                  {(workshopSheetPreview.workflow?.steps || []).map((step) => (
                    <Stack key={step.code} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>{step.label || step.code}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDateTime(step.completedAt)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkshopSheetPreview(null)}>Fermer</Button>
          <Button variant="contained" startIcon={<PrintRoundedIcon />} onClick={() => handlePrintWorkshopSheet(workshopSheetPreview)}>Imprimer</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pickingOpen} onClose={() => !pickingSaving && setPickingOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handlePickingSubmit}>
          <DialogTitle>Picking de verre</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Scanne le JOB, puis un ou deux verres réservés. Le picking valide la réception des verres et fait avancer le suivi.
              </Alert>
              {pickingTarget && (
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                    <Typography variant="caption" color="text.secondary">Verre(s) attendu(s)</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 800 }}>
                      {getReservedLensSides(pickingTarget).map(formatReservedLens).join(' | ')
                        || getStockItemLabel(pickingTarget.lens?.stockItemId)
                        || '—'}
                    </Typography>
                  </CardContent>
                </Card>
              )}
              <TextField
                autoFocus={!pickingTarget}
                label="Scanner un JOB"
                value={pickingForm.jobNumber}
                onChange={(event) => setPickingForm((current) => ({ ...current, jobNumber: event.target.value }))}
                disabled={Boolean(pickingTarget)}
                required
                fullWidth
              />
              <TextField
                autoFocus={Boolean(pickingTarget)}
                label="Code-barres verre 1"
                value={pickingForm.firstBarcode}
                onChange={(event) => setPickingForm((current) => ({ ...current, firstBarcode: event.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Code-barres verre 2 (si nécessaire)"
                value={pickingForm.secondBarcode}
                onChange={(event) => setPickingForm((current) => ({ ...current, secondBarcode: event.target.value }))}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPickingOpen(false)} disabled={pickingSaving}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={pickingSaving || !pickingForm.jobNumber || !pickingForm.firstBarcode}>
              {pickingSaving ? 'Validation…' : 'Valider le picking'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={sendOpen} onClose={() => !sending && setSendOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handleSendSubmit}>
          <DialogTitle>
            <Stack direction="row" spacing={1} alignItems="center">
              <LocalShippingRoundedIcon color="primary" />
              <span>Envoi verre</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Prépare le verre pour son retour au magasin. Après validation, le dossier pourra être ajouté à un bon de livraison.
              </Alert>
              <TextField
                autoFocus
                label="Scanner un JOB"
                value={sendForm.jobNumber}
                onChange={(event) => setSendForm((current) => ({ ...current, jobNumber: event.target.value }))}
                required
                fullWidth
              />
              <Autocomplete
                options={stockSettings.mountingTypes}
                value={stockSettings.mountingTypes.includes(sendForm.mountingType) ? sendForm.mountingType : null}
                onChange={(_event, value) => setSendForm((current) => ({ ...current, mountingType: value || '' }))}
                disableClearable
                renderInput={(params) => <TextField {...params} label="Type de montage" required />}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSendOpen(false)} disabled={sending}>Annuler</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={sending || !sendForm.jobNumber.trim() || !stockSettings.mountingTypes.includes(sendForm.mountingType)}
            >
              {sending ? 'Validation…' : 'Préparer l’envoi'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={supplyOpen} onClose={() => setSupplyOpen(false)} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={handleSupplySubmit}>
          <DialogTitle>Approvisionner le stock</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Scanne le code-barres d’un verre déjà présent dans le stock puis indique la quantité reçue.
              </Alert>
              <TextField
                name="siteId"
                label="Atelier"
                select
                value={supplyForm.siteId}
                onChange={(event) => setSupplyForm((current) => ({ ...current, siteId: event.target.value }))}
                required
                disabled={!isAdmin}
              >
                {workshopSites.map((workshopSite) => (
                  <MenuItem key={workshopSite._id} value={workshopSite._id}>
                    {getSiteLabel(workshopSite)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                autoFocus
                label="Quantité à ajouter"
                type="number"
                value={supplyForm.quantity}
                onChange={(event) => setSupplyForm((current) => ({ ...current, quantity: Number(event.target.value) }))}
                inputProps={{ min: 1 }}
                required
                fullWidth
              />
              <TextField
                label="Code-barres"
                value={supplyForm.barcode}
                onChange={(event) => setSupplyForm((current) => ({ ...current, barcode: event.target.value }))}
                required
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSupplyOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={supplying}>
              {supplying ? 'Approvisionnement…' : 'Valider'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        itemLabel={`le verre ${deleteTarget?.barcode || ''}`}
        description={`Confirmer la suppression du verre ${
          deleteTarget?.barcode || deleteTarget?.label || ''
        } ?`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget._id)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
